// @flow

import aliases from 'react-emoji-render/data/aliases';
import emojiAsciiAliases from 'react-emoji-render/data/asciiAliases';
import moment from 'moment';
import axios from 'axios';

import { getAuthUrl } from '../../api/url';
import { showToast } from '../../features/notifications/functions.web';
import { NOTIFICATION_TIMEOUT } from '../../features/notifications/constants';
import { getConferenceName } from '../base/conference/functions';
import { getLocalizedDateFormatter } from '../base/i18n/dateUtil';
import i18next from '../base/i18n/i18next';
import { escapeRegexp, getBaseUrl } from '../base/util/helpers';

import { sendMessage, setFileUploadedPercentageValue } from './actions.any';
import { MESSAGE_TYPE_ERROR, MESSAGE_TYPE_LOCAL, TIMESTAMP_FORMAT } from './constants';

/**
 * An ASCII emoticon regexp array to find and replace old-style ASCII
 * emoticons (such as :O) with the new Unicode representation, so that
 * devices and browsers that support them can render these natively
 * without a 3rd party component.
 *
 * NOTE: this is currently only used on mobile, but it can be used
 * on web too once we drop support for browsers that don't support
 * unicode emoji rendering.
 */
const ASCII_EMOTICON_REGEXP_ARRAY: Array<Array<Object>> = [];

/**
 * An emoji regexp array to find and replace alias emoticons
 * (such as :smiley:) with the new Unicode representation, so that
 * devices and browsers that support them can render these natively
 * without a 3rd party component.
 *
 * NOTE: this is currently only used on mobile, but it can be used
 * on web too once we drop support for browsers that don't support
 * unicode emoji rendering.
 */
const SLACK_EMOJI_REGEXP_ARRAY: Array<Array<Object>> = [];

(function() {
    for (const [ key, value ] of Object.entries(aliases)) {

        // Add ASCII emoticons
        const asciiEmoticons = emojiAsciiAliases[key];

        if (asciiEmoticons) {
            const asciiEscapedValues = asciiEmoticons.map(v => escapeRegexp(v));

            const asciiRegexp = `(${asciiEscapedValues.join('|')})`;

            // Escape urls
            const formattedAsciiRegexp = key === 'confused'
                ? `(?=(${asciiRegexp}))(:(?!//).)`
                : asciiRegexp;

            ASCII_EMOTICON_REGEXP_ARRAY.push([ new RegExp(formattedAsciiRegexp, 'g'), value ]);
        }

        // Add slack-type emojis
        const emojiRegexp = `\\B(${escapeRegexp(`:${key}:`)})\\B`;

        SLACK_EMOJI_REGEXP_ARRAY.push([ new RegExp(emojiRegexp, 'g'), value ]);
    }
})();

/**
 * Replaces ASCII and other non-unicode emoticons with unicode emojis to let the emojis be rendered
 * by the platform native renderer.
 *
 * @param {string} message - The message to parse and replace.
 * @returns {string}
 */
export function replaceNonUnicodeEmojis(message: string) {
    let replacedMessage = message;

    for (const [ regexp, replaceValue ] of SLACK_EMOJI_REGEXP_ARRAY) {
        replacedMessage = replacedMessage.replace(regexp, replaceValue);
    }

    for (const [ regexp, replaceValue ] of ASCII_EMOTICON_REGEXP_ARRAY) {
        replacedMessage = replacedMessage.replace(regexp, replaceValue);
    }

    return replacedMessage;
}

/**
 * Selector for calculating the number of unread chat messages.
 *
 * @param {Object} state - The redux state.
 * @returns {number} The number of unread messages.
 */
export function getUnreadCount(state: Object) {
    const { lastReadMessage, messages } = state['features/chat'];
    const messagesCount = messages.length;

    if (!messagesCount) {
        return 0;
    }

    let reactionMessages = 0;
    let lastReadIndex;

    if (navigator.product === 'ReactNative') {
        // React native stores the messages in a reversed order.
        lastReadIndex = messages.indexOf(lastReadMessage);

        for (let i = 0; i < lastReadIndex; i++) {
            if (messages[i].isReaction) {
                reactionMessages++;
            }
        }

        return lastReadIndex - reactionMessages;
    }

    lastReadIndex = messages.lastIndexOf(lastReadMessage);

    for (let i = lastReadIndex + 1; i < messagesCount; i++) {
        if (messages[i].isReaction) {
            reactionMessages++;
        }
    }

    return messagesCount - (lastReadIndex + 1) - reactionMessages;
}

/**
 * Selector for calculating the number of unread chat messages.
 *
 * @param {Object} state - The redux state.
 * @returns {number} The number of unread messages.
 */
export function getUnreadMessagesCount(state: Object) {
    const { nbUnreadMessages } = state['features/chat'];

    return nbUnreadMessages;
}

/**
 * Get whether the chat smileys are disabled or not.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean} The disabled flag.
 */
export function areSmileysDisabled(state: Object) {
    const disableChatSmileys = state['features/base/config']?.disableChatSmileys === true;

    return disableChatSmileys;
}

export async function uploadFile(file, store, fileUploadInProgress) {
    if (!file) {
        return;
    }

    const MAX_FILE_SIZE_FOR_UPLOAD = 314572800; // 300 MB = 300 X 1024 X 1024 bytes

    // use the option fileUploadInProgress if we wish to allow only one file upload at a time
    // and unless the file is completely uploaded, new files cannot be uploaded
    if(fileUploadInProgress) {
        alert(i18next.t('fileupload.fileuploadinprogress'));
        return;
    }

    if(file.size > MAX_FILE_SIZE_FOR_UPLOAD) {
        alert(i18next.t('fileupload.maxfilesizeexceeded'));
        return;
    }

    let _apiBase = getAuthUrl(store.getState());

    const serverURL = getBaseUrl();
    const dispatch = store.dispatch;
    const formData = new FormData();

    

    const roomName =  getConferenceName(store.getState()).replace(/\s+/g, '').toLowerCase() // strip all spaces and case convert to lowercase
    
    // we use moment data to append timestamp while uploading a file
    const ts = moment().format('YYYYMMDDhhmmss');
    const extn = file.name.split('.').pop();
    const fname = file.name.split(`.${extn}`)[0];
    const fileSize = file.size;
    const fnameWithTS = fname + '_' + ts + '.' + extn;

    let config = {
        onUploadProgress: (progressEvent) => {
            let percent = Math.round( (progressEvent.loaded * 100) / progressEvent.total);

            // redux magic here
            dispatch(setFileUploadedPercentageValue(percent, fnameWithTS, fileSize));
            
        }
    }
    
    // update the formdata object
    formData.append(file.name, file);

    // name that includes the timestamp during which file was uploaded
    formData.append('filename', fnameWithTS);

    // attach the roomName to the file upload button
    formData.append('roomName', roomName);

    // code to send to vmapi
    try {
        const resp = await axios.post(`${_apiBase}/uploads`, formData, config);

        if(resp.status === 200) {
            // the URL of the server should be adjusted accordingly

            // we use encodeURIComponent to ensure that spaces and special characters in filename is well-replaced to represent a URL
            const newFileUrl = `${serverURL}download/files/${encodeURIComponent(roomName)}/${encodeURIComponent(resp.data.fileName)}`;

            // dispatch sendMessage action to display the URL of the uploaded file as a message
            dispatch(sendMessage(newFileUrl));

            // reset fileUploadPercentage to 0;
            // dispatch a new action for this, right now it is just resetting the original values
            dispatch(setFileUploadedPercentageValue(0, '', 0));
        }
        
    } catch(err) {
        // show a toast error message in case of file upload error
        console.log("Error is: ", err);
        showToast({
            title: i18next.t('fileupload.error'), // need to use translated strings here
            timeout: NOTIFICATION_TIMEOUT.MEDIUM,
            icon: 'error',
            animation: false });
    }
}

/**
 * Returns the timestamp to display for the message.
 *
 * @param {IMessage} message - The message from which to get the timestamp.
 * @returns {string}
 */
export function getFormattedTimestamp(message) {
    return getLocalizedDateFormatter(new Date(message.timestamp))
        .format(TIMESTAMP_FORMAT);
}

/**
 * Generates the message text to be rendered in the component.
 *
 * @param {IMessage} message - The message from which to get the text.
 * @returns {string}
 */
export function getMessageText(message) {
    return message.messageType === MESSAGE_TYPE_ERROR
        ? i18next.t('chat.error', {
            error: message.message
        })
        : message.message;
}

/**
 * Returns the message that is displayed as a notice for private messages.
 *
 * @param {IMessage} message - The message to be checked.
 * @returns {string}
 */
export function getPrivateNoticeMessage(message) {
    return i18next.t('chat.privateNotice', {
        recipient: message.messageType === MESSAGE_TYPE_LOCAL ? message.recipient : i18next.t('chat.you')
    });
}
